/**
 * OrgServicesSection — the Services & specialties picker zone (UX-13 S3
 * extraction from OrganizationDetail; composition preserved).
 *
 * Three labelled picker rows on the shared Section/Field register
 * (SectionBlock card + Field display under the S3 delegations): the
 * Block 6.B.2 pickers render read-only chips for CM and the edit
 * affordances for direct-write roles — the pickers own that branch,
 * this section just composes them.
 */

import { type ReactNode } from 'react';
import { Section } from '@/components/peace-hub-port/ResourceDetail';
import { SubcategoryPicker } from '@/components/SubcategoryPicker';
import { SpecialtyPicker } from '@/components/SpecialtyPicker';
import { ServiceAreaPicker } from '@/components/ServiceAreaPicker';
import type { ProviderDetail } from '@/lib/types';

export interface OrgServicesSectionProps {
  provider: ProviderDetail;
  providerId: string;
  canEdit: boolean;
  onMutated: () => Promise<void>;
}

/** A named chip group: an 11px uppercase sage-400 label above its chips (§3). */
function ChipGroup({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-sage-400">
        {label}
      </div>
      {children}
    </div>
  );
}

export function OrgServicesSection({ provider, providerId, canEdit, onMutated }: OrgServicesSectionProps): ReactNode {
  return (
    <Section title="Services & specialties">
      <div className="flex flex-col gap-4">
        <ChipGroup label="Subcategories">
          <SubcategoryPicker
            providerId={providerId}
            selected={provider.subcategories}
            onMutated={onMutated}
            canEdit={canEdit}
          />
        </ChipGroup>
        <ChipGroup label="Specialties">
          <SpecialtyPicker
            providerId={providerId}
            selected={provider.specialties}
            onMutated={onMutated}
            canEdit={canEdit}
          />
        </ChipGroup>
        <ChipGroup label="Service areas">
          <ServiceAreaPicker
            providerId={providerId}
            selected={provider.service_areas}
            onMutated={onMutated}
            canEdit={canEdit}
          />
        </ChipGroup>
      </div>
    </Section>
  );
}
